import apiClient from '../../shared/services/apiClient';

/**
 * CDN Service (Admin Version)
 *
 * Handles administrative CDN operations with full CRUD capabilities.
 * Admins can view, create, update, and delete CDN resources.
 *
 * All routes use /api/admin/cdn/* prefix
 */
class CDNAdminService {
  // ==========================================
  // Domain Management (Full CRUD)
  // ==========================================

  /**
   * Get all domains (admin can see all users' domains)
   */
  async getDomains() {
    try {
      const response = await apiClient.get('/api/admin/cdn/domains');
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get domain details with hostnames and rules
   * @param {string|number} domainOrId - Domain name (string) or domain ID (number)
   */
  async getDomain(domainOrId) {
    try {
      const params = typeof domainOrId === 'number' || /^\d+$/.test(domainOrId)
        ? { id: domainOrId }
        : { domain: domainOrId };
      const response = await apiClient.get('/api/admin/cdn/domains/get', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Create a new domain
   */
  async createDomain(data) {
    try {
      const response = await apiClient.post('/api/admin/cdn/domains/create', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Update domain
   */
  async updateDomain(data) {
    try {
      const response = await apiClient.post('/api/admin/cdn/domains/update', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Delete domain
   */
  async deleteDomain(id) {
    try {
      const response = await apiClient.delete('/api/admin/cdn/domains/delete', { data: { id } });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // ==========================================
  // Domain Lifecycle Management (Admin Only)
  // ==========================================

  /**
   * 标记域名归属已验证
   */
  async markDomainVerified(id) {
    try {
      const response = await apiClient.post('/api/admin/cdn/domains/mark-verified', { id });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Suspend CDN domain
   * @param {number} id - Domain ID
   * @param {string} reason - Suspension reason
   */
  async suspendDomain(id, reason = '') {
    try {
      const response = await apiClient.post('/api/admin/cdn/domains/suspend', { id, reason });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Resume CDN domain
   * @param {number} id - Domain ID
   */
  async resumeDomain(id) {
    try {
      const response = await apiClient.post('/api/admin/cdn/domains/resume', { id });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Terminate CDN domain
   * @param {number} id - Domain ID
   * @param {boolean} hardDelete - Whether to permanently delete
   */
  async terminateDomain(id, hardDelete = false) {
    try {
      const response = await apiClient.post('/api/admin/cdn/domains/terminate', { id, hard_delete: hardDelete });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Activate CDN domain
   * @param {number} id - Domain ID
   */
  async activateDomain(id) {
    try {
      const response = await apiClient.post('/api/admin/cdn/domains/activate', { id });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Change domain owner
   * @param {number} id - Domain ID
   * @param {number} newUserId - New user ID
   */
  async changeDomainOwner(id, newUserId) {
    try {
      const response = await apiClient.post('/api/admin/cdn/domains/change-owner', { id, new_user_id: newUserId });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Purge domain cache
   * @param {number} id - Domain ID
   * @param {array} paths - Optional specific paths to purge
   */
  async purgeDomainCache(id, paths = []) {
    try {
      const response = await apiClient.post('/api/admin/cdn/domains/purge-cache', { id, paths });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // ==========================================
  // Hostname Management (Full CRUD)
  // ==========================================

  /**
   * Get all hostnames for a domain
   */
  async getHostnames(domainId) {
    try {
      const response = await apiClient.get('/api/admin/cdn/hostnames', {
        params: { domain_id: domainId }
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get hostname details
   */
  async getHostname(id) {
    try {
      const response = await apiClient.get('/api/admin/cdn/hostnames/get', { params: { id } });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Create a new hostname
   */
  async createHostname(data) {
    try {
      const response = await apiClient.post('/api/admin/cdn/hostnames/create', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Update hostname
   */
  async updateHostname(data) {
    try {
      const response = await apiClient.post('/api/admin/cdn/hostnames/update', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Delete hostname
   */
  async deleteHostname(id) {
    try {
      const response = await apiClient.delete('/api/admin/cdn/hostnames/delete', { data: { id } });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // ==========================================
  // Rules Management (Full CRUD)
  // ==========================================

  /**
   * Get rules for a domain
   * @param {string|number} domainOrId - Domain name (string) or domain ID (number)
   */
  async getRules(domainOrId) {
    try {
      const params = typeof domainOrId === 'number'
        ? { domain_id: domainOrId }
        : { domain: domainOrId };
      const response = await apiClient.get('/api/admin/cdn/rules', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get rule details
   */
  async getRule(id) {
    try {
      const response = await apiClient.get('/api/admin/cdn/rules/get', { params: { id } });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Create a new rule
   */
  async createRule(data) {
    try {
      const response = await apiClient.post('/api/admin/cdn/rules/create', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Update rule
   */
  async updateRule(data) {
    try {
      const response = await apiClient.post('/api/admin/cdn/rules/update', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Delete rule
   */
  async deleteRule(id) {
    try {
      const response = await apiClient.delete('/api/admin/cdn/rules/delete', { data: { id } });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // ==========================================
  // WAF Management (Full CRUD)
  // ==========================================

  /**
   * Get domain-level WAF config (enabled_rulesets / mode / score_threshold / paranoia_level).
   * admin Firewall.jsx 依赖此方法；后端复用 user ConfigController（对 admin 放行）。
   */
  async getWafConfig(domainId) {
    try {
      const params = domainId ? { domain_id: domainId } : {};
      const response = await apiClient.get('/api/admin/cdn/waf-config', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Save domain-level WAF config.
   */
  async saveWafConfig(payload) {
    try {
      const response = await apiClient.post('/api/admin/cdn/waf-config/save', payload);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get all WAF rules
   */
  async getWafRules() {
    try {
      const response = await apiClient.get('/api/admin/cdn/waf');
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get WAF rule details
   */
  async getWafRule(id) {
    try {
      const response = await apiClient.get('/api/admin/cdn/waf/get', { params: { id } });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Create WAF rule
   */
  async createWafRule(data) {
    try {
      const response = await apiClient.post('/api/admin/cdn/waf/create', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Update WAF rule
   */
  async updateWafRule(data) {
    try {
      const response = await apiClient.post('/api/admin/cdn/waf/update', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Delete WAF rule
   */
  async deleteWafRule(id) {
    try {
      const response = await apiClient.delete('/api/admin/cdn/waf/delete', { data: { id } });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Toggle WAF for a domain
   */
  async toggleWaf(domain, enabled) {
    try {
      const response = await apiClient.post('/api/admin/cdn/waf/toggle', { domain, enabled });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // ==========================================
  // CDN Groups Management (Full CRUD)
  // ==========================================

  /** Plan → ordered groups + caps + overflow (admin) */
  async getPlanGroups(productId) {
    try {
      const response = await apiClient.get('/api/admin/cdn/plan-groups', { params: { product_id: productId } });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  async savePlanGroups(data) {
    try {
      const response = await apiClient.post('/api/admin/cdn/plan-groups/save', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get all CDN groups
   * @param {number} userId - Optional user ID filter
   * @param {string} status - Optional status filter
   */
  async getCdnGroups(userId = null, status = null) {
    try {
      const params = {};
      if (userId) params.user_id = userId;
      if (status) params.status = status;
      const response = await apiClient.get('/api/admin/cdn/groups', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get single CDN group
   * @param {number} id - Group ID
   */
  async getCdnGroup(id) {
    try {
      const response = await apiClient.get('/api/admin/cdn/groups/get', { params: { id } });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get available DNS zones for CDN groups (uid=1 only)
   */
  async getAvailableCdnZones() {
    try {
      const response = await apiClient.get('/api/admin/cdn/groups/available-zones');
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Create new CDN group
   */
  async createCdnGroup(data) {
    try {
      const response = await apiClient.post('/api/admin/cdn/groups/create', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Update CDN group
   */
  async updateCdnGroup(data) {
    try {
      const response = await apiClient.post('/api/admin/cdn/groups/update', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Delete CDN group
   */
  async deleteCdnGroup(id) {
    try {
      const response = await apiClient.delete('/api/admin/cdn/groups/delete', { data: { id } });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // ==========================================
  // CDN Group Subscription Grants (Admin)
  // Per-subscription authorization to a CDN group
  // ==========================================

  /**
   * Get subscription grants for a group (or by subscription)
   * @param {object} params - { group_id } or { subscription_id }
   */
  async getSubscriptionGrants(params = {}) {
    try {
      const response = await apiClient.get('/api/admin/cdn/groups/subscription-grants', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Create a subscription -> group grant
   * @param {object} data - { subscription_id, group_id }
   */
  async createSubscriptionGrant(data) {
    try {
      const response = await apiClient.post('/api/admin/cdn/groups/subscription-grants', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Delete a subscription -> group grant
   * @param {object} data - { id } or { subscription_id, group_id }
   */
  async deleteSubscriptionGrant(data) {
    try {
      const response = await apiClient.delete('/api/admin/cdn/groups/subscription-grants', { data });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // ==========================================
  // CDN Lines Management (Full CRUD)
  // ==========================================

  /**
   * Get all CDN lines
   * @param {number} groupId - Optional group ID filter
   * @param {string} status - Optional status filter
   */
  async getCdnLines(groupId = null, status = null) {
    try {
      const params = {};
      if (groupId) params.group_id = groupId;
      if (status) params.status = status;
      const response = await apiClient.get('/api/admin/cdn/lines', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get single CDN line
   * @param {number} id - Line ID
   */
  async getCdnLine(id) {
    try {
      const response = await apiClient.get('/api/admin/cdn/lines/get', { params: { id } });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Create new CDN line
   */
  async createCdnLine(data) {
    try {
      const response = await apiClient.post('/api/admin/cdn/lines/create', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Update CDN line
   */
  async updateCdnLine(data) {
    try {
      const response = await apiClient.post('/api/admin/cdn/lines/update', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Delete CDN line
   */
  async deleteCdnLine(id) {
    try {
      const response = await apiClient.delete('/api/admin/cdn/lines/delete', { data: { id } });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Add node to line
   */
  async addNodeToLine(lineId, nodeId, priority = 100, weight = 100) {
    try {
      const response = await apiClient.post('/api/admin/cdn/lines/add-node', {
        line_id: lineId,
        node_id: nodeId,
        priority,
        weight
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Remove node from line
   */
  async removeNodeFromLine(lineId, nodeId) {
    try {
      const response = await apiClient.delete('/api/admin/cdn/lines/remove-node', {
        data: {
          line_id: lineId,
          node_id: nodeId
        }
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // ==========================================
  // Nodes Management (Admin Only)
  // ==========================================

  /**
   * Get all CDN nodes
   */
  async getNodes() {
    try {
      const response = await apiClient.get('/api/admin/nodes');
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get node details
   */
  async getNode(id) {
    try {
      const response = await apiClient.get('/api/admin/nodes/get', { params: { id } });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Create new node
   */
  async createNode(data) {
    try {
      const response = await apiClient.post('/api/admin/nodes/create', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Update node
   */
  async updateNode(data) {
    try {
      const response = await apiClient.post('/api/admin/nodes/update', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Delete node
   */
  async deleteNode(id) {
    try {
      const response = await apiClient.delete('/api/admin/nodes/delete', { data: { id } });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get node health status
   */
  async getNodeHealth(id) {
    try {
      const response = await apiClient.get('/api/admin/nodes/health', { params: { id } });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Sync configurations to node
   */
  async syncNode(id, type = 'config') {
    try {
      const response = await apiClient.post('/api/admin/nodes/sync', { id, type });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get node site versions (paginated)
   * @param {number} nodeId - Node ID
   * @param {number} page - Page number (default 1)
   * @param {number} pageSize - Page size (default 20)
   * @param {string} status - Filter by sync status (synced, pending, failed)
   */
  async getNodeSiteVersions(nodeId, page = 1, pageSize = 20, status = null) {
    try {
      const params = { node_id: nodeId, page, page_size: pageSize };
      if (status) params.status = status;
      const response = await apiClient.get('/api/admin/nodes/site-versions', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Push upgrade to CDN nodes
   */
  async upgradeNodes(packages = ['lua'], nodeIds = []) {
    try {
      const response = await apiClient.post('/api/admin/nodes/upgrade', {
        packages,
        node_ids: nodeIds,
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get per-node task sync status (redis-queue task system)
   * @param {object} params - Optional filters: { node_id, domain_id, state, lag_threshold, recent }
   * Returns: { success, data: { nodes: [...], summary: {...} } }
   */
  async getNodeSyncStatus(params = {}) {
    try {
      const response = await apiClient.get('/api/admin/nodes/sync-status', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * 任务中心：按 task_id 聚合的近期任务列表（含失败节点）。
   * @param {object} params - { type, domain_id, state, from, to, limit }
   */
  async getTasksStatus(params = {}) {
    try {
      const response = await apiClient.get('/api/admin/cdn/tasks/status', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * 单任务详情：该任务在每个节点的逐节点状态。
   */
  async getTaskDetail(taskId) {
    try {
      const response = await apiClient.get(`/api/admin/cdn/tasks/status/${encodeURIComponent(taskId)}`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * 重新推送失败/pending 任务（可选 node_id 只重推某节点）。
   */
  async retryTask(taskId, nodeId = null) {
    try {
      const body = nodeId ? { node_id: nodeId } : {};
      const response = await apiClient.post(`/api/admin/cdn/tasks/${encodeURIComponent(taskId)}/retry`, body);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * 重置任务卡住/失败行状态（可选 node_id）。
   */
  async resetTask(taskId, nodeId = null) {
    try {
      const body = nodeId ? { node_id: nodeId } : {};
      const response = await apiClient.post(`/api/admin/cdn/tasks/${encodeURIComponent(taskId)}/reset`, body);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get node WS/remote status
   */
  async getNodeWsStatus(nodeId) {
    try {
      const params = nodeId ? { node_id: nodeId } : {};
      const response = await apiClient.get('/api/admin/nodes/ws-status', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // ==========================================
  // Configuration Management (Admin Only)
  // ==========================================

  /**
   * Rebuild all domain configurations
   */
  async rebuildConfigs() {
    try {
      const response = await apiClient.post('/api/admin/config/rebuild');
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Sync configurations to all nodes
   */
  async syncConfigs() {
    try {
      const response = await apiClient.post('/api/admin/config/sync');
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // ==========================================
  // Global Settings (Admin Only)
  // ==========================================

  /**
   * Get global CDN settings
   */
  async getGlobalSettings() {
    try {
      const response = await apiClient.get('/api/admin/cdn/global-settings');
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Update global CDN setting
   */
  async updateGlobalSetting(data) {
    try {
      const response = await apiClient.post('/api/admin/cdn/global-settings', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // ==========================================
  // WAF Rulesets Management (Admin)
  // ==========================================

  /**
   * Get all WAF rulesets (Admin)
   */
  // ── 运维告警 ──
  async getOpsAlerts() {
    try {
      const response = await apiClient.get('/api/admin/ops/alerts');
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  async saveOpsAlertChannels(cfg) {
    try {
      const response = await apiClient.post('/api/admin/ops/alerts/channels', cfg);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  async testOpsAlert() {
    try {
      const response = await apiClient.post('/api/admin/ops/alerts/test', {});
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  async runOpsCheck() {
    try {
      const response = await apiClient.post('/api/admin/ops/alerts/check', {});
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // ── 全局防火墙规则(平台级,所有域名生效) ──
  async getGlobalRules() {
    try {
      const response = await apiClient.get('/api/admin/cdn/global-rules');
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  async saveGlobalRule(rule) {
    try {
      const response = await apiClient.post('/api/admin/cdn/global-rules/save', rule);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  async toggleGlobalRule(id, enabled) {
    try {
      const response = await apiClient.post('/api/admin/cdn/global-rules/toggle', { id, enabled });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  async deleteGlobalRule(id) {
    try {
      const response = await apiClient.post('/api/admin/cdn/global-rules/delete', { id });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  async getWafRulesetsAdmin() {
    try {
      const response = await apiClient.get('/api/admin/waf-rulesets');
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get WAF rulesets grouped by category (Admin)
   */
  async getWafRulesetsGroupedAdmin() {
    try {
      const response = await apiClient.get('/api/admin/waf-rulesets/grouped');
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get current WAF rulesets version (Admin)
   */
  async getWafRulesetsVersion() {
    try {
      const response = await apiClient.get('/api/admin/waf-rulesets/version');
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get single WAF ruleset (Admin)
   */
  async getWafRuleset(id) {
    try {
      const response = await apiClient.get(`/api/admin/waf-rulesets/${id}`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Create WAF ruleset (Admin)
   */
  async createWafRuleset(data) {
    try {
      const response = await apiClient.post('/api/admin/waf-rulesets', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Update WAF ruleset (Admin)
   */
  async updateWafRuleset(id, data) {
    try {
      const response = await apiClient.put(`/api/admin/waf-rulesets/${id}`, data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Delete (soft delete) WAF ruleset (Admin)
   */
  async deleteWafRuleset(id) {
    try {
      const response = await apiClient.delete(`/api/admin/waf-rulesets/${id}`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Restore WAF ruleset (Admin)
   */
  async restoreWafRuleset(id) {
    try {
      const response = await apiClient.post(`/api/admin/waf-rulesets/${id}/restore`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // ==========================================
  // CDN Line Records Management (Admin)
  // ==========================================

  /**
   * Get CDN line records
   */
  async getLineRecords(groupId = null, nodeId = null, status = null, perPage = null) {
    try {
      const params = {};
      if (perPage) params.per_page = perPage;
      if (groupId) params.group_id = groupId;
      if (nodeId) params.node_id = nodeId;
      if (status) params.status = status;
      const response = await apiClient.get('/api/admin/cdn/line-records', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get a single line record
   */
  async getLineRecord(id) {
    try {
      const response = await apiClient.get(`/api/admin/cdn/line-records/${id}`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Create line record
   */
  async createLineRecord(data) {
    try {
      const response = await apiClient.post('/api/admin/cdn/line-records', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Update line record
   */
  async updateLineRecord(id, data) {
    try {
      const response = await apiClient.put(`/api/admin/cdn/line-records/${id}`, data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Delete line record
   */
  // 以某条线路为模板新建（服务端复制全部配置，data 覆盖；name 必填）
  async copyLineRecord(sourceId, data) {
    try {
      const response = await apiClient.post(`/api/admin/cdn/line-records/${sourceId}/copy`, data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  async deleteLineRecord(id) {
    try {
      const response = await apiClient.delete(`/api/admin/cdn/line-records/${id}`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Batch create line records
   */
  async batchCreateLineRecords(records) {
    try {
      const response = await apiClient.post('/api/admin/cdn/line-records/batch', { records });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Preview line records for a group
   */
  async previewLineRecords(groupId, testIp = null) {
    try {
      const params = { group_id: groupId };
      if (testIp) params.test_ip = testIp;
      const response = await apiClient.get('/api/admin/cdn/line-records/preview', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Sync line records to DNS (manual trigger)
   */
  async syncLineRecords(groupId) {
    try {
      const response = await apiClient.post('/api/admin/cdn/line-records/sync', { group_id: groupId });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // ==========================================
  // CDN DNS Sync Management (Admin)
  // ==========================================

  /**
   * Sync all CDN groups' DNS records
   */
  async syncAllDns() {
    try {
      const response = await apiClient.post('/api/admin/cdn/dns-sync/all');
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Sync a single group's DNS records
   */
  async syncGroupDns(groupId) {
    try {
      const response = await apiClient.post(`/api/admin/cdn/dns-sync/group/${groupId}`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Sync a single product's DNS records
   */
  async syncProductDns(productId) {
    try {
      const response = await apiClient.post(`/api/admin/cdn/dns-sync/product/${productId}`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Cleanup orphaned DNS records
   */
  async cleanupDns() {
    try {
      const response = await apiClient.post('/api/admin/cdn/dns-sync/cleanup');
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get DNS sync status/logs
   */
  async getDnsSyncStatus(groupId = null) {
    try {
      const params = {};
      if (groupId) params.group_id = groupId;
      const response = await apiClient.get('/api/admin/cdn/dns-sync/status', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // ==========================================
  // Traffic Analytics (Admin)
  // ==========================================

  /**
   * Get traffic statistics overview
   */
  async getTrafficOverview(startTime, endTime, domainId = null) {
    try {
      const params = { startTime, endTime };
      if (domainId) params.domain_id = domainId;
      const response = await apiClient.get('/api/admin/traffic/stats', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get traffic map data (by country/region)
   */
  async getTrafficMap(startTime, endTime, domainId = null) {
    try {
      const params = { startTime, endTime };
      if (domainId) params.domain_id = domainId;
      const response = await apiClient.get('/api/admin/traffic/map', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get traffic cache performance (time series)
   */
  async getTrafficCachePerformance(startTime, endTime, domainId = null) {
    try {
      const params = { startTime, endTime };
      if (domainId) params.domain_id = domainId;
      const response = await apiClient.get('/api/admin/traffic/cache', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get top URLs
   */
  async getTrafficTopUrls(startTime, endTime, domainId = null, limit = 10) {
    try {
      const params = { startTime, endTime, limit };
      if (domainId) params.domain_id = domainId;
      const response = await apiClient.get('/api/admin/traffic/top-urls', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data||error.message };
    }
  }

  /**
   * Get HTTP status codes statistics
   */
  async getTrafficStatusCodes(startTime, endTime, domainId = null) {
    try {
      const params = { startTime, endTime };
      if (domainId) params.domain_id = domainId;
      const response = await apiClient.get('/api/admin/traffic/status-codes', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get browser statistics
   */
  async getTrafficBrowsers(startTime, endTime, domainId = null) {
    try {
      const params = { startTime, endTime };
      if (domainId) params.domain_id = domainId;
      const response = await apiClient.get('/api/admin/traffic/browsers', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // ==========================================
  // CDN Node Groups Management (Admin)
  // ==========================================

  /**
   * Get all node groups
   */
  async getNodeGroups() {
    try {
      const response = await apiClient.get('/api/admin/node-groups');
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get single node group
   */
  async getNodeGroup(id) {
    try {
      const response = await apiClient.get('/api/admin/node-groups/get', { params: { id } });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Create node group
   */
  async createNodeGroup(data) {
    try {
      const response = await apiClient.post('/api/admin/node-groups/create', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Update node group
   */
  async updateNodeGroup(data) {
    try {
      const response = await apiClient.post('/api/admin/node-groups/update', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Delete node group
   */
  async deleteNodeGroup(id) {
    try {
      const response = await apiClient.delete('/api/admin/node-groups/delete', { data: { id } });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get L2 nodes for a group
   */
  async getNodeGroupL2Nodes(groupId) {
    try {
      const response = await apiClient.get('/api/admin/node-groups/l2-nodes', { params: { group_id: groupId } });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Assign node to group
   */
  async assignNodeToGroup(nodeId, groupId) {
    try {
      const response = await apiClient.post('/api/admin/node-groups/assign-node', { node_id: nodeId, group_id: groupId });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // ==========================================
  // Hostname SSL Management (Admin)
  // ==========================================

  async enableHostnameSsl(id, enabled, sslType = 'auto') {
    try {
      const response = await apiClient.post('/api/admin/cdn/hostnames/ssl/enable', {
        id, enabled, ssl_type: sslType
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  async requestHostnameAcme(id, validationType = 'http-01') {
    try {
      const response = await apiClient.post('/api/admin/cdn/hostnames/ssl/request-acme', {
        id, validation_type: validationType
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  async getHostnameAcmeStatus(id) {
    try {
      const response = await apiClient.get('/api/admin/cdn/hostnames/ssl/acme-status', {
        params: { id }
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  async uploadHostnameSsl(id, certificate, privateKey) {
    try {
      const response = await apiClient.post('/api/admin/cdn/hostnames/ssl/upload', {
        id, certificate, private_key: privateKey
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // ==========================================
  // Domain-level SSL Management (Admin)
  // ==========================================

  async getSSLCertificate(domain) {
    try {
      const response = await apiClient.get('/api/admin/cdn/ssl/get', { params: { domain } });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  async requestSSLCertificate(data) {
    try {
      const response = await apiClient.post('/api/admin/cdn/ssl/request', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  async uploadSSLCertificate(data) {
    try {
      const response = await apiClient.post('/api/admin/cdn/ssl/upload', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  async renewSSLCertificate(data) {
    try {
      const response = await apiClient.post('/api/admin/cdn/ssl/renew', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  async revokeSSLCertificate(data) {
    try {
      const response = await apiClient.post('/api/admin/cdn/ssl/revoke', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  async syncSSLCertificate(data) {
    try {
      const response = await apiClient.post('/api/admin/cdn/ssl/sync_cert', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // Certificate Library
  async getCertLibrary(userId) {
    try {
      const params = userId ? { user_id: userId } : {};
      const response = await apiClient.get('/api/admin/cdn/cert-library', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  async uploadCertToLibrary(data) {
    try {
      const response = await apiClient.post('/api/admin/cdn/cert-library/upload', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  async deleteCertFromLibrary(id) {
    try {
      const response = await apiClient.delete('/api/admin/cdn/cert-library/delete', { data: { id } });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  async applyCertToHostname(certId, hostnameId) {
    try {
      const response = await apiClient.post('/api/admin/cdn/cert-library/apply', {
        cert_id: certId,
        hostname_id: hostnameId
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // ==========================================
  // CDN Workers Management (Full CRUD) — mirrors user cdnService
  // Routes: /api/admin/cdn/workers/*
  // ==========================================

  async getWorkers(domainId = null, page = 1, pageSize = 20) {
    try {
      const params = { page, page_size: pageSize };
      if (domainId) params.domain_id = domainId;
      const response = await apiClient.get('/api/admin/cdn/workers', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  async getWorker(id) {
    try {
      const response = await apiClient.get('/api/admin/cdn/workers/detail', { params: { id } });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  async saveWorker(workerData) {
    try {
      const response = await apiClient.post('/api/admin/cdn/workers/save', workerData);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  async deleteWorker(id) {
    try {
      const response = await apiClient.delete('/api/admin/cdn/workers/delete', { data: { id } });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  async toggleWorker(id, enabled) {
    try {
      const response = await apiClient.post('/api/admin/cdn/workers/toggle', { id, enabled });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // Domain Workers (domain-specific worker binding)
  async getDomainWorkers(domain) {
    try {
      const response = await apiClient.get(`/api/admin/cdn/domains/${domain}/workers`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  async getAvailableWorkers(domain) {
    try {
      const response = await apiClient.get(`/api/admin/cdn/domains/${domain}/workers/available`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  async saveDomainWorker(domain, workerData) {
    try {
      const response = await apiClient.post(`/api/admin/cdn/domains/${domain}/workers/save`, workerData);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  async deleteDomainWorker(domain, id) {
    try {
      const response = await apiClient.delete(`/api/admin/cdn/domains/${domain}/workers/delete`, { data: { id } });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  async toggleDomainWorker(domain, id, enabled) {
    try {
      const response = await apiClient.post(`/api/admin/cdn/domains/${domain}/workers/toggle`, { id, enabled });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // ==========================================
  // Firewall Rules (Full CRUD) — mirrors user cdnService
  // Routes: /api/admin/cdn/firewall-rules/*
  // ==========================================

  async getFirewallRules(params) {
    try {
      const response = await apiClient.get('/api/admin/cdn/firewall-rules', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  async getFirewallRule(params) {
    try {
      const response = await apiClient.get('/api/admin/cdn/firewall-rules/get', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  async createFirewallRule(data) {
    try {
      const response = await apiClient.post('/api/admin/cdn/firewall-rules/create', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  async updateFirewallRule(data) {
    try {
      const response = await apiClient.post('/api/admin/cdn/firewall-rules/update', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  async deleteFirewallRule(data) {
    try {
      const response = await apiClient.delete('/api/admin/cdn/firewall-rules/delete', { data });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // ==========================================
  // WAF Analytics — mirrors user cdnService
  // Routes: /api/admin/waf-analytics/*
  // ==========================================

  async getWafOverview(params) {
    try {
      const response = await apiClient.get('/api/admin/waf-analytics/overview', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  async getWafTimeline(params) {
    try {
      const response = await apiClient.get('/api/admin/waf-analytics/timeline', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  async getWafTopRules(params) {
    try {
      const response = await apiClient.get('/api/admin/waf-analytics/top-rules', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  async getWafTopIPs(params) {
    try {
      const response = await apiClient.get('/api/admin/waf-analytics/top-ips', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  async getWafLogs(params) {
    try {
      const response = await apiClient.get('/api/admin/waf-analytics/logs', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Grouped WAF ruleset catalog (used by the Firewall page ruleset selector).
   * Mirrors user getWafRulesetsGrouped — reuses ConfigController on the backend.
   */
  async getWafRulesetsGrouped() {
    try {
      const response = await apiClient.get('/api/admin/cdn/waf-rulesets/grouped');
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // ==========================================
  // Origin CA Certificate Management — mirrors user cdnService
  // Routes: /api/admin/cdn/origin-cert/*
  // ==========================================

  async createOriginCert(data) {
    try {
      const response = await apiClient.post('/api/admin/cdn/origin-cert/create', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  async getOriginCerts(page = 1, perPage = 20, extraParams = {}) {
    try {
      const response = await apiClient.get('/api/admin/cdn/origin-cert/list', {
        params: { page, per_page: perPage, ...extraParams }
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  async getOriginCert(id) {
    try {
      const response = await apiClient.get('/api/admin/cdn/origin-cert/get', { params: { id } });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  async revokeOriginCert(id) {
    try {
      const response = await apiClient.post('/api/admin/cdn/origin-cert/revoke', { id });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  async deleteOriginCert(id) {
    try {
      const response = await apiClient.delete('/api/admin/cdn/origin-cert/delete', { params: { id } });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  async getOriginCaCert() {
    try {
      const response = await apiClient.get('/api/admin/cdn/origin-cert/ca');
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // ==========================================
  // Error Pages (账号级,按 domain_id 的所有者)
  // ==========================================

  async getErrorPages(domainId) {
    try {
      const response = await apiClient.get('/api/admin/cdn/error-pages', { params: { domain_id: domainId } });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  async getErrorPageTemplates(domainId) {
    try {
      const response = await apiClient.get('/api/admin/cdn/error-pages/templates', { params: { domain_id: domainId } });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  async saveErrorPage(domainId, data) {
    try {
      const response = await apiClient.post('/api/admin/cdn/error-pages/save', { ...data, domain_id: domainId }, { params: { domain_id: domainId } });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  async deleteErrorPage(domainId, id) {
    try {
      const response = await apiClient.delete('/api/admin/cdn/error-pages/delete', { params: { domain_id: domainId }, data: { id, domain_id: domainId } });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // ==========================================
  // Diagnostics
  // ==========================================

  /** 入队后轮询结果(通常 10-15s);params = { hostname, domain_id } */
  async runDiagnostics(params) {
    try {
      const started = await apiClient.post('/api/admin/cdn/diagnostics/run', params);
      if (!started.data?.success) return started.data;
      const id = started.data.data?.id;
      for (let i = 0; i < 60; i++) {
        await new Promise((r) => setTimeout(r, 1500));
        const res = await apiClient.get('/api/admin/cdn/diagnostics/result', { params: { id } });
        if (!res.data?.success) return res.data;
        if (res.data.data?.status === 'done') return { success: true, data: res.data.data.result };
      }
      return { success: false, error: '诊断超时' };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }
}

export default new CDNAdminService();
